import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Terms of Service - Sales Intercept</title>
        <meta name="description" content="Read the Sales Intercept Terms of Service. This agreement governs your use of our platform, services, and subscriptions. Understand your rights and obligations." />
      </Helmet>
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>
          <p className="text-gray-600 mb-6">Effective Date: April 22, 2025</p>

          <div className="prose prose-gray max-w-none">
            <p className="mb-6">
              These Terms of Service (the "Terms" or "Agreement") govern your access to and use of the
              websites located at salesintercept.com, developersquad.com, any sub-domains,
              associated web or mobile applications, and related services, including subscriptions and
              white-labeled third-party services (collectively, the "Platform"), provided by Developer
              Squad, Inc., doing business as Sales Intercept ("Sales Intercept," "we," "us," or "our"). By
              accessing or using the Platform, you ("Customer," "you," or "your") acknowledge that you
              have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you
              do not agree, please do not use the Platform.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">1. Scope</h2>
            <p className="mb-6">
              These Terms apply to all users of the Platform, including those who purchase
              subscriptions, one-time services (e.g., setup fees), or use free features. Certain services
              offered through the Platform are provided by third-party providers under a white-label
              arrangement, as described in Section 5.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">2. Platform Use</h2>
            <h3 className="text-xl font-bold mt-6 mb-3">2.1 Eligibility</h3>
            <p className="mb-6">
              You must be at least 18 years old (or 16 in the EEA with parental consent) to use the Platform.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">2.2 Account</h3>
            <p className="mb-6">
              You may need to create an account to access certain features. You are responsible for
              maintaining the confidentiality of your account credentials and for all activities under your
              account.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">2.3 Acceptable Use</h3>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-6">
              <li>Use the Platform for unlawful purposes or in violation of these Terms.</li>
              <li>Post or transmit harmful, defamatory, or infringing content.</li>
              <li>Attempt to disrupt, hack, or gain unauthorized access to the Platform.</li>
              <li>Use automated tools (e.g., bots, scrapers) without our permission.</li>
            </ul>
            <p className="mb-6">
              We reserve the right to suspend or terminate your access for violations of this section.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">3. Services and Subscriptions</h2>
            <h3 className="text-xl font-bold mt-6 mb-3">3.1 Services Offered</h3>
            <p className="mb-6">
              The Platform provides software-as-a-service (SaaS) solutions, custom development,
              integrations, support, consulting, and white-labeled third-party services. Services may be
              subject to setup fees, monthly or annual subscriptions, or other charges as specified at
              the time of purchase.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">3.2 No Service Level Agreements (SLAs)</h3>
            <p className="mb-6">
              Sales Intercept does not guarantee the availability, performance, or continuity of any
              services, including third-party services, and no SLAs are provided.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">3.3 White-Labeled Third-Party Services</h3>
            <p className="mb-6">
              Certain services are provided by third-party providers under a white-label arrangement.
              These services are offered "as is," without warranties or guarantees of uptime,
              performance, or continuity. Sales Intercept is not liable for any interruptions,
              discontinuations, or failures of third-party services.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">3.4 Service Substitution</h3>
            <p className="mb-6">
              If a third-party service is discontinued or unavailable, Sales Intercept may, at its
              discretion, substitute it with a comparable service or offer remedies as described in
              Section 4.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">3.5 Modifications</h3>
            <p className="mb-6">
              We may modify, suspend, or discontinue any service or feature of the Platform at any time
              with or without notice, subject to the remedies in Section 4.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">4. Payment Terms</h2>
            <h3 className="text-xl font-bold mt-6 mb-3">4.1 Fees</h3>
            <p className="mb-6">
              If you purchase a subscription or other paid service, you agree to pay all fees specified at
              the time of purchase ("Fees"). Fees may include setup fees, recurring subscription
              charges, or other costs. Fees are billed in advance and are non-refundable except as
              stated below.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">4.2 Recurring Payments</h3>
            <p className="mb-6">
              If you arrange for recurring payments, your chosen payment method will be charged
              automatically on each renewal date until you cancel. You may cancel recurring payments
              through your account dashboard or by contacting us at legal@salesintercept.com.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">4.3 Non-Refundable Fees; Credits</h3>
            <p className="mb-6">
              Except as required by law or at our discretion, all Fees are non-refundable. If a service is
              permanently discontinued and no comparable alternative is available, Sales Intercept may,
              at its discretion, issue a pro-rated refund for the remaining subscription term or provide a
              pro-rated credit applicable to other services. Credits are issued upon your request to
              legal@salesintercept.com, expire 12 months from issuance, and are non-transferable.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">4.4 Taxes</h3>
            <p className="mb-6">
              You are responsible for any taxes applicable to your purchases.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">4.5 Payment Failure</h3>
            <p className="mb-6">
              Failure to pay Fees when due may result in suspension or termination of your access to
              paid features.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">5. Third-Party Links and Services</h2>
            <h3 className="text-xl font-bold mt-6 mb-3">5.1 Third-Party Links</h3>
            <p className="mb-6">
              The Platform may contain links to third-party websites or services that we do not control.
              We are not responsible for their content, privacy policies, or practices.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">5.2 White-Labeled Services</h3>
            <p className="mb-6">
              As noted in Section 3.3, white-labeled third-party services are provided "as is." Sales
              Intercept is not responsible for any downtime, discontinuation, or performance issues
              caused by third-party providers. In the event of a service discontinuation, we may offer a
              pro-rated credit or substitute service as described in Section 4.3.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">5.3 Disclosure</h3>
            <p className="mb-6">
              We disclose at the point of sale and in our marketing materials that certain services are
              white-labeled and subject to third-party performance risks.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">6. Disclaimers</h2>
            <p className="mb-6">
              THE PLATFORM AND ALL SERVICES, INCLUDING WHITE-LABELED THIRD-PARTY SERVICES, ARE
              PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, SALES
              INTERCEPT DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
              WE DO NOT WARRANT THAT THE PLATFORM OR SERVICES WILL BE UNINTERRUPTED,
              ERROR-FREE, SECURE, OR FREE OF VIRUSES. NO SLAs ARE PROVIDED FOR ANY SERVICES.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">7. Limitation of Liability</h2>
            <p className="mb-6">
              IN NO EVENT WILL SALES INTERCEPT, ITS AFFILIATES, OR LICENSORS BE LIABLE FOR ANY
              INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR
              FOR LOSS OF PROFITS, DATA, OR GOODWILL, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH
              DAMAGES. SALES INTERCEPT'S TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR
              RELATING TO THESE TERMS, THE PLATFORM, OR ANY SERVICES, INCLUDING THIRD-PARTY
              SERVICES, WILL NOT EXCEED THE AMOUNT PAID BY YOU TO SALES INTERCEPT FOR THE
              AFFECTED SERVICE DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE
              TO THE CLAIM. Some jurisdictions do not allow certain limitations of liability, so some of
              the above may not apply to you.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">8. Indemnification</h2>
            <p className="mb-6">
              You will indemnify, defend, and hold harmless Sales Intercept and its officers, directors,
              employees, and agents from and against any claims, damages, losses, liabilities, costs,
              and expenses (including reasonable attorneys' fees) arising from or relating to: (a) your
              use of the Platform, (b) your User Content, or (c) your breach of these Terms.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">9. Force Majeure</h2>
            <p className="mb-6">
              Sales Intercept is not liable for any failure to perform due to causes beyond our
              reasonable control, including, but not limited to, third-party service disruptions, internet
              outages, acts of God, war, terrorism, or government actions.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">10. Termination</h2>
            <h3 className="text-xl font-bold mt-6 mb-3">10.1 By You</h3>
            <p className="mb-6">
              You may stop using the Platform at any time. To cancel a subscription, follow the
              instructions in Section 4.2.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">10.2 By Us</h3>
            <p className="mb-6">
              We may suspend or terminate your access to the Platform or specific services, in whole or
              in part, at any time and for any reason, including for actual or suspected violation of
              these Terms or third-party service discontinuations. If a third-party service is
              discontinued, we may terminate access to that service with notice and, at our discretion,
              offer a pro-rated credit or substitute service as described in Section 4.3.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">10.3 Effect of Termination</h3>
            <p className="mb-6">
              Upon termination, your right to use the Platform or affected services will immediately
              cease, but Sections 2.3, 6, 7, 8, 11, and 12 will survive.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">11. Governing Law & Dispute Resolution</h2>
            <p className="mb-6">
              These Terms are governed by the laws of the State of New York, without regard to its
              conflict-of-laws rules. Any dispute arising out of or relating to these Terms or the
              Platform will be resolved exclusively in the state or federal courts located in Erie County,
              New York, and each party consents to the personal jurisdiction and venue of those courts.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">12. General Terms</h2>
            <h3 className="text-xl font-bold mt-6 mb-3">12.1 Entire Agreement</h3>
            <p className="mb-6">
              These Terms, together with our Privacy Policy, constitute the entire agreement between
              you and Sales Intercept regarding the Platform and supersede all prior agreements.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">12.2 Severability</h3>
            <p className="mb-6">
              If any provision of these Terms is held invalid, the remaining provisions will remain in
              full force and effect.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">12.3 Waiver</h3>
            <p className="mb-6">
              Our failure to enforce any right or provision of these Terms will not constitute a waiver
              of that right or provision.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">12.4 Assignment</h3>
            <p className="mb-6">
              You may not assign or transfer your rights under these Terms without our prior written
              consent. We may freely assign these Terms.
            </p>

            <h3 className="text-xl font-bold mt-6 mb-3">12.5 Updates</h3>
            <p className="mb-6">
              We may update these Terms periodically. If changes are material, we will notify you via
              email or website announcement. Continued use of the Platform after updates constitutes
              acceptance of the revised Terms.
            </p>

            <h2 className="text-2xl font-bold mt-8 mb-4">13. Contact Us</h2>
            <p className="mb-6">If you have questions about these Terms, contact us at:</p>
            <address className="mb-6 not-italic">
              Sales Intercept / Developer Squad, Inc.<br />
              7954 Transit Road #232<br />
              Williamsville, NY 14221 USA<br />
              Email: legal@salesintercept.com
            </address>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default TermsOfService;